import { Grid, Paper, Typography, capitalize } from '@material-ui/core'
import { useQueryClient } from '@tanstack/react-query'
import { FormikProvider, useFormik } from 'formik'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { useBreadcrumbsData } from '../../Layout/breadcrumbsContext/useBreadcrumbsData/useBreadcrumbsData'
import { useNonprofitsData } from '../../Layout/nonprofitsContext/useNonprofitsData/useNonprofitsData'

import { CreateRegistryProps } from '@percent/admin-dashboard/api/actions/registries/registries.types'
import { Tab, Tabs } from '@percent/lemonade'
import { ValidationDocument, ValidationSubmissionDocument } from '@percent/admin-dashboard/api/types'
import { ReactComponent as ExpandLogo } from '@percent/admin-dashboard/common/assets/images/expand.svg'
import { ReactComponent as PencilSVG } from '@percent/admin-dashboard/common/assets/images/pencil.svg'
import { ErrorView, Status } from '@percent/admin-dashboard/common/components'
import { Documents } from '@percent/admin-dashboard/common/components/documents/Documents'
import { ListItemButton } from '@percent/admin-dashboard/common/components/listItemButton/ListItemButton'
import {
  useGetDgrHelpers,
  useQuery,
  useQueryList,
  useRequiredParams,
  useValidationRequestsAccess,
  useValidationSubmissionsAccess
} from '@percent/admin-dashboard/common/hooks'
import { useActivityTags } from '@percent/admin-dashboard/common/hooks/useActivityTags/useActivityTags'
import { DecoratedActivitySubTag } from '@percent/admin-dashboard/common/hooks/useActivityTags/useActivityTags.types'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { APIErrorHandler } from '@percent/admin-dashboard/common/library/APIErrorHandler'
import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import { getRegion } from '@percent/admin-dashboard/common/utility/getRegion'
import { renderAddressString } from '@percent/admin-dashboard/common/utility/renderAddressString'
import { sortEducationSubtags } from '@percent/admin-dashboard/common/utility/sortEducationSubtags'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'
import { colors } from '@percent/admin-dashboard/identity'
import { OrganisationTypes } from '@percent/domain/verifications'
import { sanitizeStringToRegex } from '@percent/utility'
import { VR_AUTOMATION_ACCOUNT_ID } from '@percent/admin-dashboard/constants/qc'
import styles from './NonprofitDetail.module.scss'
import { ApproveModal, RejectModal } from './dialogs'
import { RegistryModal } from './dialogs/registryModal/RegistryModal'
import { CountrySpecificData, SelectedRegistryData } from './dialogs/registryModal/RegistryModal.types'
import { SubmitQcModal } from './dialogs/submitQcModal/SubmitQcModal'
import { SubTagsModal } from './dialogs/subTagsModal/SubTagsModal'
import { SubTagModalFormValues } from './dialogs/subTagsModal/SubTagsModal.types'
import { ReviewWebsiteModal } from './dialogs/reviewWebsiteModal/ReviewWebsiteModal'
import { UpdateWebsiteModal } from './dialogs/updateWebsiteModal/UpdateWebsiteModal'
import { UpdateEmailModal } from './dialogs/updateEmailModal/UpdateEmailModal'
import { EligibilityTagsSection } from './eligibilityTags/EligibilityTagsSection'
import { VRFraudChecks } from './fraudChecks/VRFraudChecks'
import { NonProfitDetailHeader } from './header/NonProfitDetailHeader'
import { NonProfitDetailListItem } from './nonProfitDetailListItem/NonProfitDetailListItem'
import { OrganisationTypeSection } from './organisationType/OrganisationTypeSection'
import { RalphReasoning } from './ralphReasoning/RalphReasoning'
import { RegistrySection } from './registrySection/RegistrySection'
import { RelatedOrganisations } from './relatedOrganisations/RelatedOrganisations'
import { RelationshipDropdownValues, RelationshipFormValues } from './relatedOrganisations/RelatedOrganisations.types'
import { SimilarOrganisations } from './similarOrganisations/SimilarOrganisations'
import {
  SimilarOrganisationActionValues,
  SimilarOrganisationFormValues
} from './similarOrganisations/SimilarOrganisations.types'
import {
  ValidationRequestTimeline,
  validationRequestLogsQueryKey
} from './validationRequestTimeline/ValidationRequestTimeline'
import { RequestDocumentation } from './requestDocumentation/RequestDocumentation'
import { disableConfirm } from './utils/disableConfirm'
import { isNonprofitAndEducationOnlyConfig } from './utils/isNonprofitAndEducationOnlyConfig'
import { WebsiteSection } from './websiteSection/WebsiteSection'

export function NonprofitDetail() {
  const { t } = useTranslation()
  const vrReviewWebsiteFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_VR_REVIEW_WEBSITE)
  const documentRequestFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_VR_DOCUMENT_REQUEST)
  const submitQcFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_SUBMIT_QC)
  const { canUpdate, canAccept, canReject, canQc } = useValidationRequestsAccess()
  // The documents shown here belong to the linked validation submission, so they are
  // gated by the validation_submissions domain rather than validation_requests.
  const { canReadDocuments } = useValidationSubmissionsAccess()
  const { setPath } = useBreadcrumbsData()
  const { adminService, registriesService } = useServices()
  const { validation, setValidation } = useNonprofitsData()
  const { mapCountrySpecificDataToDgr } = useGetDgrHelpers()

  const [isApproveModalOpened, setIsApproveModalOpened] = useState(false)
  const [isRejectModalOpened, setIsRejectModalOpened] = useState(false)
  const [isSubTagsModalOpened, setIsSubTagsModalOpened] = useState(false)
  const [isWebsiteModalOpened, setWebsiteModalOpened] = useState(false)
  const [isEditWebsiteModalOpened, setEditWebsiteModalOpened] = useState(false)
  const [isEmailModalOpened, setEmailModalOpened] = useState(false)
  const [isRegistryModalOpened, setRegistryModalOpened] = useState(false)
  const [isSubmitQcModalOpened, setIsSubmitQcModalOpened] = useState(false)
  const [registryData, setRegistryData] = useState<CreateRegistryProps | null>(null)
  const [registryDataError, setRegistryDataError] = useState('')
  const [selectedRegistryData, setSelectedRegistryData] = useState<SelectedRegistryData>(null)
  const [documents, setDocuments] = useState<ValidationDocument[] | ValidationSubmissionDocument[]>([])
  const [subTags, setSubTags] = useState<DecoratedActivitySubTag[]>([])
  const [countrySpecificData, setCountrySpecificData] = useState<CountrySpecificData>(null)

  const toast = useToast()

  const { activityTags } = useActivityTags({ filterByEducation: true })
  const { id } = useRequiredParams()
  // QUERIES

  const [{ data, isLoading, errorMessage }, { refresh }] = useQuery(adminService.getValidationRequest, { id })

  const { pathname } = useLocation()
  const navigate = useNavigate()
  const detailsTab = `/validations/${id}`
  const timelineTab = `${detailsTab}/timeline`
  const activeTab = pathname === timelineTab ? timelineTab : detailsTab

  const queryClient = useQueryClient()
  // The timeline owns its own react-query entry, so the decisions that write a log have to invalidate it as well.
  const refreshAfterDecision = useCallback(() => {
    refresh()
    queryClient.invalidateQueries({ queryKey: validationRequestLogsQueryKey(id) })
  }, [refresh, queryClient, id])

  const [
    { dataOrNull: relatedOrganisations, isLoading: isLoadingRelatedOrganisations },
    { query: refreshRelatedOrganisations }
  ] = useQueryList(
    adminService.getRelatedOrganisations,
    {
      registry: selectedRegistryData?.registry.id,
      registryId: selectedRegistryData?.organisationRegistryId,
      relationshipType: 'branch'
    },
    false
  )

  const [
    { dataOrNull: similarOrganisations, isLoading: isLoadingSimilarOrganisations },
    { query: refreshSimilarOrganisations, resetBody: resetSimilarOrganisationsQuery }
  ] = useQueryList(
    adminService.getSimilarOrganisations,
    {
      registry: selectedRegistryData?.registry.id,
      registryId: selectedRegistryData?.organisationRegistryId,
      countryCode: validation?.countryCode,
      name: validation?.name,
      city: validation?.city,
      website: validation?.website,
      address: renderAddressString({
        addressLine1: validation?.addressLine1,
        addressLine2: validation?.addressLine2,
        city: validation?.city,
        postal: validation?.postal,
        state: validation?.state
      })
    },
    false
  )

  const [
    { dataOrNull: validationSubmissionDocuments, isLoading: isLoadingValidationSubmissionDocuments },
    { query: refreshValidationSubmissionDocuments }
  ] = useQueryList(
    adminService.getValidationSubmissionDocuments,
    {
      validationSubmissionId: validation?.validationSubmissionId
    },
    false
  )

  // FORMS

  const organisationTypeForm = useFormik<{ organisationTypes: OrganisationTypes[] }>({
    initialValues: {
      organisationTypes: []
    },
    validationSchema: yup.object().shape({
      organisationTypes: yup.array().of(yup.string()).min(1)
    }),
    onSubmit: () => {}
  })

  const subTagsForm = useFormik<SubTagModalFormValues>({
    initialValues: {
      subTags: [],
      insufficientInformation: false,
      notEducation: false
    },
    validationSchema: () => {
      if (isOnlyEducationOrganisation) {
        return yup.object().shape({
          subTags: yup.array().when(['insufficientInformation', 'notEducation'], {
            is: false,
            then: yup.array().of(yup.string()).min(1),
            otherwise: yup.array().of(yup.string().max(0))
          }),
          insufficientInformation: yup.boolean(),
          notEducation: yup.boolean()
        })
      }

      return yup.object().shape({})
    },

    onSubmit: () => {}
  })

  const relationshipForm = useFormik<RelationshipFormValues>({
    initialValues: {
      type: '',
      organisationId: ''
    },
    validationSchema: () => {
      if (relatedOrganisations?.length) {
        return yup.object().shape({
          type: yup
            .string()
            .oneOf([
              RelationshipDropdownValues.MERGE,
              RelationshipDropdownValues.BRANCH_CHILD,
              RelationshipDropdownValues.BRANCH_PARENT,
              RelationshipDropdownValues.FISCAL_SPONSORSHIP_CHILD,
              RelationshipDropdownValues.FISCAL_SPONSORSHIP_PARENT
            ])
            .required('Required Type'),
          organisationId: yup.string().when(['type'], {
            is: (type: RelationshipDropdownValues) =>
              [
                RelationshipDropdownValues.MERGE,
                RelationshipDropdownValues.BRANCH_CHILD,
                RelationshipDropdownValues.FISCAL_SPONSORSHIP_CHILD
              ].includes(type),
            then: schema => schema.required('Required')
          }),
          organisation: yup.object().when(['organisationId'], {
            is: (value: string) => !!value,
            then: schema =>
              schema
                .shape({
                  relationship: yup.string(),
                  result: yup.object().shape({
                    id: yup.string().required()
                  })
                })
                .required('Required')
          })
        })
      }

      return yup.object().shape({})
    },
    onSubmit: () => {}
  })

  const similarOrganisationForm = useFormik<SimilarOrganisationFormValues>({
    initialValues: {
      action: '',
      organisationId: ''
    },
    validationSchema: () => {
      if (selectedRegistryData?.registryInformationNotFound) {
        return yup.object().shape({
          action: yup
            .string()
            .oneOf([SimilarOrganisationActionValues.IGNORE, SimilarOrganisationActionValues.MERGE])
            .required('Required'),
          organisationId: yup.string().when(['action'], {
            is: (action: SimilarOrganisationActionValues) => [SimilarOrganisationActionValues.MERGE].includes(action),
            then: schema => schema.required('Required')
          }),
          organisation: yup.object().when(['organisationId'], {
            is: (value: string) => !!value,
            then: schema =>
              schema
                .shape({
                  name: yup.string(),
                  organisationTypes: yup.array().of(yup.string()).min(1),
                  website: yup.string(),
                  countryCode: yup.string(),
                  registry: yup.string(),
                  registryId: yup.string()
                })
                .required('Required')
          })
        })
      }

      return yup.object().shape({})
    },
    onSubmit: () => {}
  })

  // HELPER FUNCTIONS

  const getRegistryData = useCallback(
    (registryId: string) =>
      registriesService
        .getRegistriesById({ id: registryId })
        .then(regData => {
          setRegistryData(regData?.data?.data)
        })
        .catch(error => {
          setRegistryDataError(APIErrorHandler(error?.response?.data?.error))
        }),
    [registriesService]
  )

  const getValidationStatus = () => {
    if (validation?.acceptedAt || (validation?.organisationId && validation?.rejectedAt)) {
      return Status.variants.APPROVED
    }

    if (validation?.rejectedAt) {
      return Status.variants.REJECTED
    }

    return Status.variants.PENDING
  }

  const name = data ? data.name : ''

  const registryIdDoesNotMatchRegistryRegex = () => {
    if (selectedRegistryData?.registryInformationNotFound) {
      return false
    }

    return (
      !!selectedRegistryData?.organisationRegistryId &&
      !sanitizeStringToRegex(registryIdRegex ?? '').test(selectedRegistryData?.organisationRegistryId)
    )
  }

  // VARIABLES

  const nonprofitName = capitalize(String(name))
  const oracleValidationLink = `oracleValidationLink.${validation?.countryCode}` as LocaleKey
  const status = getValidationStatus()
  const inputsEditable = ![Status.variants.APPROVED, Status.variants.REJECTED].includes(status)
  // Affordances that write back to the request itself (edit email, edit website, trigger
  // a website review) additionally need can_update; the registry and sub-tag pickers only
  // build up the accept payload locally, so they stay on inputsEditable alone.
  const requestEditable = inputsEditable && canUpdate
  const canViewDocuments = !validation?.validationSubmissionId || canReadDocuments

  const registryIdRegex = selectedRegistryData?.registry?.id
    ? selectedRegistryData?.registry?.regex
    : validation?.fieldConstraints?.fields?.registryId?.regex

  const isOnlyEducationOrganisation =
    organisationTypeForm.values.organisationTypes?.length === 1 &&
    organisationTypeForm.values.organisationTypes.includes(OrganisationTypes.Education)

  const isNonprofitOrganisation = organisationTypeForm.values.organisationTypes.includes(OrganisationTypes.Nonprofit)

  const isApproved = [Status.variants.APPROVED].includes(status)
  const isRejected = [Status.variants.REJECTED].includes(status)

  const canSubmitQc =
    submitQcFeatureFlag &&
    canQc &&
    !!VR_AUTOMATION_ACCOUNT_ID &&
    validation?.reviewedBy?.account === VR_AUTOMATION_ACCOUNT_ID &&
    !isOnlyEducationOrganisation

  const isIndianValidationRequest = validation?.countryCode === 'IND'
  const isIndianNonprofit = isIndianValidationRequest && isNonprofitOrganisation

  const isAustralianValidationRequest = validation?.countryCode === 'AUS'

  const hideRelatedOrganisationsSection =
    isRejected ||
    (isApproved && !validation?.organisation?.relationships?.length) ||
    (!isApproved && !selectedRegistryData?.registry?.id)

  const hideSimilarOrganisationsSection =
    isRejected ||
    isApproved ||
    (!isApproved && !selectedRegistryData?.registryInformationNotFound && !isIndianNonprofit) ||
    (!isApproved && !similarOrganisations?.length)

  const insufficientSubTagsInformation =
    subTagsForm.values.insufficientInformation || (isApproved && validation?.organisationSubTags === null)

  const isConfirmDisabled = disableConfirm({
    validation,
    selectedRegistryData,
    registryIdDoesNotMatchRegistryRegex: registryIdDoesNotMatchRegistryRegex(),
    relatedOrganisations,
    relationshipFormType: relationshipForm.values.type,
    relationshipFormErrors: relationshipForm.errors,
    organisationTypeFormErrors: organisationTypeForm.errors,
    isLoadingRelatedOrganisations,
    similarOrganisations,
    similarOrganisationFormAction: similarOrganisationForm.values.action,
    similarOrganisationFormErrors: similarOrganisationForm.errors,
    isLoadingSimilarOrganisations,
    isOnlyEducationOrganisation,
    subTagsFormErrors: subTagsForm.errors,
    subTagsFormValues: subTagsForm.values
  })

  // SIDE EFFECTS
  useEffect(() => {
    if (data) {
      setValidation(data)
    }

    if (validation?.organisation) {
      getRegistryData(validation?.organisation?.registry)
    }
  }, [data, getRegistryData, setValidation, validation, refreshValidationSubmissionDocuments])

  useEffect(() => {
    if (data?.validationSubmissionId && canReadDocuments) {
      refreshValidationSubmissionDocuments({ validationSubmissionId: data?.validationSubmissionId })
    }
  }, [data, refreshValidationSubmissionDocuments, canReadDocuments])

  useEffect(() => {
    if (isOnlyEducationOrganisation && status !== Status.variants.APPROVED) {
      setSelectedRegistryData({
        registry: {
          id: undefined,
          name: undefined,
          englishName: undefined
        },
        organisationRegistryId: validation?.organisation?.registryId ?? validation?.registryId ?? '',
        registryInformationNotFound: true
      })
    } else if (validation?.registryId || validation?.organisation?.registry) {
      const registryName =
        registryData?.name ??
        validation.organisation?.registryData?.name ??
        validation.organisation?.registry ??
        validation.registryName

      setSelectedRegistryData({
        registry: {
          id: '',
          name: registryName,
          englishName: registryData?.englishName ?? registryName
        },
        organisationRegistryId: validation?.organisation?.registryId ?? validation?.registryId ?? '',
        registryInformationNotFound: false
      })
    }
  }, [validation, registryData, organisationTypeForm.values.organisationTypes, isOnlyEducationOrganisation, status])

  useEffect(() => {
    setPath(name)

    return () => setPath(undefined)
  }, [setPath, name])

  useEffect(() => {
    if (
      validation?.configuration?.allowedOrganisations?.types?.[0] &&
      !organisationTypeForm.values.organisationTypes.length
    ) {
      const allowedTypes = validation.configuration.allowedOrganisations.types
      const nextOrganisationTypes = isNonprofitAndEducationOnlyConfig(allowedTypes) ? [] : allowedTypes

      organisationTypeForm.resetForm({
        values: { organisationTypes: nextOrganisationTypes },
        errors: nextOrganisationTypes.length === 0 ? { organisationTypes: 'required' } : {}
      })
    }
  }, [validation])

  useEffect(() => {
    if (isIndianNonprofit && validation?.organisation?.countrySpecificData) {
      setCountrySpecificData(validation?.organisation?.countrySpecificData)
    }

    if (isAustralianValidationRequest && validation?.organisation?.countrySpecificData) {
      setCountrySpecificData(validation?.organisation?.countrySpecificData)
    }
  }, [validation, isIndianNonprofit, isAustralianValidationRequest])

  useEffect(() => {
    if (validation?.organisationSubTags?.length && !subTagsForm.values.subTags.length) {
      const hasNoValidSubTags = !validation?.organisationSubTags.some(({ meetsCriteria }) => meetsCriteria === true)

      subTagsForm.resetForm({
        values: {
          insufficientInformation: false,
          subTags: !hasNoValidSubTags
            ? validation?.organisationSubTags?.map(({ id: organisationSubTagId }) => organisationSubTagId)
            : [],
          notEducation: hasNoValidSubTags
        }
      })
    }
  }, [validation])

  useEffect(() => {
    if (!validation) return

    const fetchDocuments = async () => {
      try {
        const documentsToFetch = validation?.documents ?? []
        const documentResponses = await Promise.all(
          documentsToFetch.map(({ url }) => adminService.getSignedUrlForDocument({ documentUrl: url }))
        )
        const updatedDocuments = documentResponses.map(response => {
          if (response.status !== 200) {
            throw new Error('Cannot fetch document.')
          }
          const { documentId, signedUrl } = response.data.data
          const oldDocument = documentsToFetch.find((doc: ValidationDocument) => doc.id === documentId)!

          return { ...oldDocument, url: signedUrl }
        }) as ValidationDocument[]

        setDocuments(updatedDocuments)
      } catch {
        toast(t('toast.documentsFetchError'), 'error')
      }
    }

    if (
      !validation.validationSubmissionId ||
      (validationSubmissionDocuments && !validationSubmissionDocuments.length)
    ) {
      fetchDocuments()
    } else {
      setDocuments(validationSubmissionDocuments ?? [])
    }
  }, [validationSubmissionDocuments, adminService, t, toast, validation])

  useEffect(() => {
    if (isOnlyEducationOrganisation && activityTags?.length) {
      setSubTags(
        [
          ...activityTags.flatMap(({ id: parentTagId, name: parentTagName, subTags: activityTagSubTags }) =>
            activityTagSubTags
              .map(subTag => ({ ...subTag, parentTagName, parentTagId }))
              .filter(({ deprecatedAt }) => !deprecatedAt)
          )
        ].sort(sortEducationSubtags)
      )
    }
  }, [activityTags, isOnlyEducationOrganisation])

  useEffect(() => {
    if (isOnlyEducationOrganisation) {
      refreshSimilarOrganisations({
        registry: selectedRegistryData?.registry?.id,
        registryId: validation?.organisation?.registryId ?? validation?.registryId ?? '',
        countryCode: validation!.countryCode,
        name: nonprofitName,
        city: validation?.city,
        website: validation?.website
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlyEducationOrganisation])

  const registryNameValue = useMemo(() => {
    if (selectedRegistryData?.registryInformationNotFound) {
      if (isOnlyEducationOrganisation && validation?.registryName) {
        return `${t('typography.notFound')} ${t('typography.providedByTheUser', {
          value: validation?.registryName
        })}`
      }

      return t('typography.notFound')
    }

    return selectedRegistryData?.registry?.name && capitalize(selectedRegistryData?.registry?.name)
  }, [
    validation,
    selectedRegistryData?.registryInformationNotFound,
    selectedRegistryData?.registry?.name,
    isOnlyEducationOrganisation,
    t
  ])

  const registryIdValue = useMemo(() => {
    if (selectedRegistryData?.registryInformationNotFound) {
      if (isOnlyEducationOrganisation && validation?.registryId) {
        return `${t('typography.notFound')} ${t('typography.providedByTheUser', {
          value: validation?.registryId
        })}`
      }

      return t('typography.notFound')
    }

    return selectedRegistryData?.organisationRegistryId
  }, [
    selectedRegistryData?.registryInformationNotFound,
    selectedRegistryData?.organisationRegistryId,
    isOnlyEducationOrganisation,
    validation,
    t
  ])

  if (registryDataError || errorMessage) {
    return <ErrorView errorMessage={errorMessage ?? registryDataError} />
  }

  return (
    <>
      <NonProfitDetailHeader
        nonprofitName={nonprofitName}
        status={status}
        setIsRejectModalOpened={setIsRejectModalOpened}
        setIsApproveModalOpened={setIsApproveModalOpened}
        notValidForProgram={!!validation?.rejectedAt && !!validation.organisationId}
        disableConfirm={isConfirmDisabled}
        isApproved={isApproved}
        isRejected={isRejected}
        disableReject={!validation}
        canAccept={canAccept}
        canReject={canReject}
        canSubmitQc={canSubmitQc}
        setIsSubmitQcModalOpened={setIsSubmitQcModalOpened}
        tabs={
          <Tabs
            activeTab={activeTab}
            onChangeTab={tab => navigate(tab)}
            items={[
              { key: detailsTab, label: t('typography.applicationDetails') },
              { key: timelineTab, label: t('typography.applicationTimeline') }
            ]}
          />
        }
      />

      <Tab index={detailsTab} value={activeTab}>
        <VRFraudChecks id={id} />

        <Grid container spacing={0} component={Paper} elevation={0} className={styles.listContainer}>
          <Grid item xs={12} className={styles.title}>
            <Typography variant="h6">{t('typography.organisationDetails')}</Typography>
          </Grid>
          <ul className={styles.detailsList}>
            <FormikProvider value={organisationTypeForm}>
              <OrganisationTypeSection status={status} validationOrganisationTypes={validation?.organisationTypes} />
            </FormikProvider>
            {isOnlyEducationOrganisation && !isRejected && (
              <EligibilityTagsSection
                subTagsForm={subTagsForm}
                subTags={subTags}
                inputsEditable={inputsEditable}
                insufficientSubTagsInformation={insufficientSubTagsInformation}
                setIsSubTagsModalOpened={setIsSubTagsModalOpened}
              />
            )}

            <NonProfitDetailListItem title={t('table.address')}>
              <span>
                {validation?.addressLine1}
                {validation?.addressLine1 && <br />}
                {validation?.addressLine2}
                {validation?.addressLine2 && <br />}
                {validation?.city}
                {validation?.city && <br />}
                {validation?.postal}
                {validation?.postal && <br />}
                {validation?.countryCode && getRegion(validation!.countryCode)}
              </span>
            </NonProfitDetailListItem>
            <WebsiteSection
              validation={validation}
              vrReviewWebsiteFeatureFlag={vrReviewWebsiteFeatureFlag}
              inputsEditable={requestEditable}
              setWebsiteModalOpened={setWebsiteModalOpened}
              setEditWebsiteModalOpened={setEditWebsiteModalOpened}
            />
            <NonProfitDetailListItem title={t('table.email')}>
              {validation?.email ? (
                <>
                  <a href={`mailto:${validation?.email}`}>{validation?.email}</a>
                  {requestEditable && (
                    <ListItemButton
                      title={t('button.editEmail')}
                      onClick={() => setEmailModalOpened(true)}
                      icon={<PencilSVG />}
                    />
                  )}
                </>
              ) : (
                <>
                  <span>{t('status.na')}</span>
                  {requestEditable && (
                    <ListItemButton
                      title={t(validation?.foundEmails?.length ? 'button.reviewEmail' : 'button.addEmail')}
                      onClick={() => setEmailModalOpened(true)}
                      icon={<PencilSVG />}
                    />
                  )}
                </>
              )}
            </NonProfitDetailListItem>

            <RegistrySection
              registryNameValue={registryNameValue}
              registryIdValue={registryIdValue}
              registryData={registryData}
              selectedRegistryData={selectedRegistryData}
              inputsEditable={inputsEditable}
              isApproved={isApproved}
              validation={validation}
              relatedOrganisations={relatedOrganisations}
              relationshipFormType={relationshipForm.values.type}
              registryIdDoesNotMatchRegistryRegex={registryIdDoesNotMatchRegistryRegex}
              setRegistryModalOpened={setRegistryModalOpened}
            />
            {isIndianNonprofit && (
              <NonProfitDetailListItem title={t('dialog.approveValidationRequest.fcraId')}>
                <span>{countrySpecificData?.fcra ?? t('typography.notProvided')}</span>
              </NonProfitDetailListItem>
            )}
            {isAustralianValidationRequest && (
              <NonProfitDetailListItem title={t('dialog.approveValidationRequest.dgrStatus')}>
                <span>{mapCountrySpecificDataToDgr(countrySpecificData) ?? t('typography.notProvided')}</span>
              </NonProfitDetailListItem>
            )}
            <NonProfitDetailListItem title={t('table.partner')}>
              <span>{validation?.partner.name}</span>
            </NonProfitDetailListItem>
            {validation?.configuration?.name && (
              <NonProfitDetailListItem title={t('table.programName')}>
                <span>{validation?.configuration?.name}</span>
              </NonProfitDetailListItem>
            )}
            <NonProfitDetailListItem title={t('table.validationRequestId')}>
              <span>{validation?.id}</span>
            </NonProfitDetailListItem>
            {validation?.organisationId && (
              <NonProfitDetailListItem title={t('table.organisationId')}>
                <Link to={`/organizations/${validation?.organisationId}`}>{validation?.organisationId}</Link>
              </NonProfitDetailListItem>
            )}
            <NonProfitDetailListItem title={t('table.requestDate')}>
              <span>{getDate(validation?.createdAt)}</span>
            </NonProfitDetailListItem>
            <NonProfitDetailListItem title={t('table.country')}>
              <div className={styles.countryDefinition}>
                <a href={t(oracleValidationLink)} target="_blank" rel="noopener noreferrer">
                  <span>{validation?.countryCode && getRegion(validation!.countryCode)}</span>
                  <ExpandLogo width="28px" height="28px" fill={colors.secondary} />
                </a>
              </div>
            </NonProfitDetailListItem>
            {validation?.reviewedBy ? (
              <NonProfitDetailListItem title={t('table.reviewedBy')}>
                <span>{validation.reviewedBy.accountEmail}</span>
              </NonProfitDetailListItem>
            ) : null}
          </ul>
        </Grid>
        {!hideSimilarOrganisationsSection && (
          <FormikProvider value={similarOrganisationForm}>
            <SimilarOrganisations
              similarOrganisations={similarOrganisations}
              isLoading={isLoadingSimilarOrganisations}
              isApproved={isApproved}
              registryInformationNotFound={selectedRegistryData?.registryInformationNotFound}
              isIndianNonprofit={isIndianNonprofit}
            />
          </FormikProvider>
        )}
        {!hideRelatedOrganisationsSection && (
          <FormikProvider value={relationshipForm}>
            <RelatedOrganisations
              relatedOrganisations={isApproved ? validation?.organisation?.relationships : relatedOrganisations}
              isLoading={isLoadingRelatedOrganisations}
              hasCorrectRegistryId={!!selectedRegistryData?.registry.id && registryIdDoesNotMatchRegistryRegex()}
              isApproved={isApproved}
              registry={selectedRegistryData?.registry.id}
              registryId={selectedRegistryData?.organisationRegistryId}
            />
          </FormikProvider>
        )}
        {canViewDocuments && (
          <div className={styles.topMargin}>
            <Documents
              isLoading={isLoading || (!!validation?.validationSubmissionId && isLoadingValidationSubmissionDocuments)}
              documents={documents}
            >
              {documentRequestFeatureFlag && validation && (
                <RequestDocumentation
                  validation={validation}
                  canRequest={requestEditable}
                  onRequested={refreshAfterDecision}
                />
              )}
            </Documents>
          </div>
        )}
        {validation?.automation && (validation.acceptedAt || validation.rejectedAt) && (
          <div className={styles.topMargin}>
            <RalphReasoning
              automation={validation.automation}
              nonprofitName={nonprofitName}
              qc={validation.automationQc}
            />
          </div>
        )}
      </Tab>
      <Tab index={timelineTab} value={activeTab}>
        <div className={styles.topMargin}>
          <ValidationRequestTimeline id={id} />
        </div>
      </Tab>
      {isApproveModalOpened && (
        <ApproveModal
          nonprofitName={nonprofitName}
          isOpened={isApproveModalOpened}
          setIsOpened={setIsApproveModalOpened}
          finishAction={refreshAfterDecision}
          validationRequestId={validation!.id}
          validationRegistryId={selectedRegistryData?.organisationRegistryId}
          validationCountryCode={validation!.countryCode}
          registryId={selectedRegistryData?.registry?.id}
          organisationTypes={organisationTypeForm.values.organisationTypes}
          validationOrganisationTypes={validation?.organisationTypes}
          relationshipType={relationshipForm?.values.type}
          relationshipOrganisation={relationshipForm?.values.organisation}
          registryInformationNotFound={selectedRegistryData?.registryInformationNotFound}
          duplicateAction={similarOrganisationForm.values.action}
          duplicateOrganisation={similarOrganisationForm.values.organisation}
          subTags={subTags}
          selectedSubTags={subTagsForm.values.subTags}
          insufficientSubTagsInformation={subTagsForm.values.insufficientInformation}
          noValidSubtags={subTagsForm.values.notEducation}
          countrySpecificData={countrySpecificData}
        />
      )}
      {isRejectModalOpened && (
        <RejectModal
          validationRequestId={validation!.id}
          nonprofitName={nonprofitName}
          isOpened={isRejectModalOpened}
          setIsOpened={setIsRejectModalOpened}
          finishAction={refreshAfterDecision}
        />
      )}
      {isWebsiteModalOpened && (
        <ReviewWebsiteModal
          validation={validation}
          isModalOpened={isWebsiteModalOpened}
          setModalOpened={setWebsiteModalOpened}
          refresh={refresh}
        />
      )}
      <UpdateWebsiteModal
        validation={validation}
        isModalOpened={isEditWebsiteModalOpened}
        setModalOpened={setEditWebsiteModalOpened}
        refresh={refresh}
      />
      <UpdateEmailModal
        validation={validation}
        isModalOpened={isEmailModalOpened}
        setModalOpened={setEmailModalOpened}
        refresh={refresh}
        foundEmails={validation?.foundEmails ?? null}
      />
      {isRegistryModalOpened && (
        <RegistryModal
          nonprofitName={nonprofitName}
          isOpened={isRegistryModalOpened}
          setIsOpened={setRegistryModalOpened}
          setSelectedRegistryData={value => {
            resetSimilarOrganisationsQuery()
            similarOrganisationForm.resetForm()
            setSelectedRegistryData(value)
          }}
          selectedRegistryData={selectedRegistryData}
          validationCountryCode={validation!.countryCode}
          city={validation?.city}
          website={validation?.website}
          regexRegistryId={registryIdRegex}
          organisationTypes={organisationTypeForm.values.organisationTypes}
          refreshRelatedOrganisations={refreshRelatedOrganisations}
          refreshSimilarOrganisations={refreshSimilarOrganisations}
          countrySpecificData={countrySpecificData}
          setCountrySpecificData={setCountrySpecificData}
          partnerName={validation?.partner.name}
        />
      )}
      {isSubTagsModalOpened && (
        <FormikProvider value={subTagsForm}>
          <SubTagsModal
            isOpened={isSubTagsModalOpened}
            setIsOpened={setIsSubTagsModalOpened}
            subTags={subTags}
            organisationName={name}
          />
        </FormikProvider>
      )}
      {validation && isSubmitQcModalOpened && (
        <SubmitQcModal
          isOpen={isSubmitQcModalOpened}
          onClose={() => setIsSubmitQcModalOpened(false)}
          validationRequestId={validation.id}
          finishAction={refresh}
        />
      )}
    </>
  )
}
